import { Component, DestroyRef, output, OnInit, ElementRef, inject } from '@angular/core';
import { UntypedFormBuilder, FormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { Company, CompanyProfile, CompanySearchResult, Customer, GuidId, NewCompany, Shareholder } from '@app/core/stingray-api/models';
import { CompaniesService, CompanyInfoService, CustomersService } from '@app/core/stingray-api/services';
import * as _ from 'lodash';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, tap } from 'rxjs/operators';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createAddressFormGroup } from '@app/shared/address-input-v2/address-input-v2-tools';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';
import { MatIconButton } from '@angular/material/button';
import { MatList, MatListItem, MatListItemIcon } from '@angular/material/list';
import { MatOption, MatLine } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { CustomerSettingsComponent } from '../../customer-settings/customer-settings.component';
import { ContactAutocompleteComponent } from '../../contact-autocomplete/contact-autocomplete.component';
import { AddressInputV2Component } from '../../address-input-v2/address-input-v2.component';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { LegalFormInputComponent } from '../../legalform-input/legalform-input.component';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix, MatPrefix } from '@angular/material/form-field';
import { CompanyAutocompleteComponent } from '../../company-autocomplete/company-autocomplete.component';
import { TranslateModule } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'unite-create-company',
    templateUrl: './create-company.component.html',
    styleUrls: ['./create-company.component.scss'],
    imports: [ReactiveFormsModule, MatIcon, TranslateModule, CompanyAutocompleteComponent, MatFormField, MatLabel, MatInput, LegalFormInputComponent, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, AddressInputV2Component, ContactAutocompleteComponent, CustomerSettingsComponent, MatPrefix, MatAutocompleteTrigger, MatProgressSpinner, MatAutocomplete, MatOption, MatList, MatListItem, MatListItemIcon, MatLine, MatIconButton, LoadingButtonComponent]
})
export class CreateCompanyComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  company = output<Company>();
  companyGroup: UntypedFormGroup;

  loadingCreate = false;
  companySearchResults: CompanySearchResult[] = [];

  // Shareholders
  searchShareholderControl: FormControl;
  searchShareholderControlIsLoading = false;
  filteredShareholders: Customer[];
  shareholders: Shareholder[] = [];

  constructor(
    private el: ElementRef,
    private googleAnalyticsService: GoogleAnalyticsService,
    private formBuilder: UntypedFormBuilder,
    private customersService: CustomersService,
    private companiesService: CompaniesService,
    private companyInfoService: CompanyInfoService) {
    this.companyGroup = this.formBuilder.group({
      chamberOfCommerceNumber: [, [Validators.required]],
      statutoryName: [],
      statutoryLocation: [],
      name: [, [Validators.required]],
      email: [],
      mobile: [],
      phone: [],
      iban: [],
      foundingDate: [],
      businessunit: [],
      address: createAddressFormGroup(this.formBuilder),
      legalForm: [],
      adviser: [],
      contact: [],
      existing: [false],
    });



    this.searchShareholderControl = this.formBuilder.control('');
    this.searchShareholderControl.valueChanges.pipe(
      tap(() => this.searchShareholderControlIsLoading = true),
      debounceTime(350),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
      switchMap((value: any) => {
        if (value && typeof value === 'string' && value.trim() !== '') {
          return this.customersService.getCustomers({
            name: value,
            types: ['Person', 'Company']
          }).pipe(map(data => data.items), catchError(() => of(null)));
        } else {
          this.searchShareholderControlIsLoading = false;
          return of(null);
        }
      })).subscribe((filteredShareholders: Customer[]) => {
        // Remove the filtered customers that are already chosen
        this.filteredShareholders = _.differenceWith(filteredShareholders, this.shareholders,
          (filteredShareholder: Customer, shareholder: Shareholder) => {
            return filteredShareholder.id === shareholder.customerId;
          });

        this.searchShareholderControlIsLoading = false;
      });
  }


  ngOnInit() {
    this.companyGroup.get('chamberOfCommerceNumber').valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {

        if (value && /^\d{8}$/.test(value)) {
          this.companyInfoService.getCompanyProfile({ chamberOfCommerceNumber: value })
            .subscribe(companyProfile => {
              this.companyProfileSelected(companyProfile);
            });
        }
      });
  }

  companyProfileSelected(companyProfile: CompanyProfile): void {
    if (companyProfile) {
        this.companyGroup.get('name').patchValue(companyProfile.name, { emitEvent: false });
        this.companyGroup.get('address').patchValue(companyProfile.address);
        this.companyGroup.get('foundingDate').setValue(moment(companyProfile.registrationDate));
        this.companyGroup.get('chamberOfCommerceNumber').setValue(companyProfile.chamberOfCommerceNumber, { emitEvent: false });
        this.companyGroup.get('statutoryName').setValue(companyProfile.statutoryName);
        this.companyGroup.get('statutoryLocation').setValue(companyProfile.statutoryLocation);
        this.companyGroup.get('legalForm').setValue(companyProfile.legalForm);
      
    }
  }
      

  create() {
    this.companyGroup.markAllAsTouched();

    if (this.companyGroup.valid) {
      this.googleAnalyticsService.trackEvent('customer', 'add', 'company');
      this.loadingCreate = true;
      
      

      const company = <NewCompany>this.companyGroup.value;

      // Normalize autocomplete result back to just a name
      if(this.companyGroup.get('name').value.name) {
        company.name = this.companyGroup.get('name').value.name;
      }

      if (company.foundingDate) {
        company.foundingDate = (<moment.Moment>this.companyGroup.value.foundingDate).locale('en').format('L');
      }
      company.shareholders = this.shareholders;

      this.companiesService.addCompany({ body: company }).pipe(finalize(() => {
        this.loadingCreate = false;

      })).subscribe((response: GuidId) => {
        company.id = response.id;
        this.company.emit(company);
      });
    } else {
      this.scrollToFirstInvalidControl();
    }
  }

  addShareholder(matAutocompleteSelectedEvent: MatAutocompleteSelectedEvent) {
    if (matAutocompleteSelectedEvent.option.value) {
      const customer: Customer = matAutocompleteSelectedEvent.option.value;

      this.shareholders.push({ customerId: customer.id, name: customer.name, share: 0, customerType: customer.type });

      this.shareholders.map((shareholder: Shareholder) => {
        shareholder.share = Math.round(100 / this.shareholders.length);
      });
    }
    this.searchShareholderControl.setValue('', { emitEvent: false });
  }

  changeShare(shareholder: Shareholder, share: number | string) {
    this.shareholders[this.shareholders.indexOf(shareholder)].share = +share;
  }

  removeShareholder(shareholder: Shareholder) {
    this.shareholders.splice(this.shareholders.indexOf(shareholder), 1);
    this.shareholders.map((shareholderUpdate: Shareholder) => {
      shareholderUpdate.share = Math.round(100 / this.shareholders.length);
    });
  }

  displaySearchValue(customer: Customer) {
    if (customer) { return customer.name; }
  }

  private scrollToFirstInvalidControl() {
    const firstInvalidControl: HTMLElement = this.el.nativeElement.querySelector(
      "form .ng-invalid"
    );

    firstInvalidControl.scrollIntoView();
  }

}
